import { ReactNode } from 'react';

import { AuthenticationScheme } from '../entities/credential.interface';

import { SidebarInput } from './connect';
import { DataType, KeyedSource, Source } from './resolvers';

export enum SidebarInputType {
  Auth = 'AUTH',
  Enum = 'ENUM',
  DynamicEnum = 'DYNAMIC_ENUM',
  Intent = 'INTENT',
  Text = 'TEXT',
  TextArea = 'TEXTAREA',
  ValueText = 'TEXT_NO_VARS',
  ValueTextArea = 'TEXTAREA_NO_VARS',
  Code = 'CODE',
  ActionButton = 'ACTION_BUTTON',
  Conditional = 'CONDITIONAL',
  DynamicConditional = 'DYNAMIC_CONDITIONAL',
  NestedList = 'NESTED_LIST',
  File = 'FILE',
  EditableDynamicEnum = 'EDITABLE_DYNAMIC_ENUM',
  EditableEnum = 'EDITABLE_ENUM',
  BooleanInput = 'BOOLEAN_INPUT',
  UserSuppliedCredential = 'USER_SUPPLIED_CREDENTIAL',
  NativeDynamicEnumInput = 'NATIVE_DYNAMIC_INPUT',
  TimeConstraintInput = 'TIME_CONSTRAINT_INPUT',
  LinesListInput = 'LinesListInput',
  LinesListDynamicInput = 'LinesListDynamicInput',
  Number = 'NUMBER',
  Email = 'EMAIL',
  URL = 'URL',
  EnumTextAreaPairInput = 'EnumTextAreaPairInput',
  FieldMapper = 'FIELD_MAPPER',
  ComboInput = 'COMBO_INPUT',
  Password = 'PASSWORD',
  Switch = 'SWITCH',
}

interface BaseInput {
  id: string;
  title: string;
  subtitle?: ReactNode;
  placeholder?: string;
  /**
   * if not provided then treated as true
   */
  required?: boolean;
  defaultValue?: any;
  documentationLink?: string;
  documentationLinkLabel?: string;
  /**
   * hide input in sidebarsection
   */
  hidden?: boolean;
  /**
   * can not change value if readOnly
   */
  readOnly?: boolean;
}

export interface IntegrationConnectInput extends BaseInput {
  type:
    | SidebarInputType.ValueText
    | SidebarInputType.Password
    | SidebarInputType.DynamicEnum
    | SidebarInputType.ComboInput
    | SidebarInputType.Enum
    | SidebarInputType.Switch
    | SidebarInputType.ValueTextArea;
  inputType?: string;
  prefixLabel?: string;
  suffixLabel?: string;
  sourceType?: string;
  fallbackText?: string;
  hiddenInConnect?: boolean;
}

type EnumSection<T = any> = {
  title: string;
  items: EnumInputValue<T>[];
};

type EnumInputValue<T = any> = {
  value: T;
  label: string;
  /**
   * subtitle property is for displaying a little helper text to explain the item in dropdown
   */
  subtitle?: string;
};

export enum TokenType {
  ACCESS_TOKEN = 'ACCESS_TOKEN',
  REFRESH_TOKEN = 'REFRESH_TOKEN',
  BOT_TOKEN = 'BOT_TOKEN',
  KLAVIYO_API_KEY = 'KLAVIYO_API_KEY',
  MARKETO_CLIENT_ID = 'MARKETO_CLIENT_ID',
  MARKETO_CLIENT_SECRET = 'MARKETO_CLIENT_SECRETI',
  MARKETO_ENDPOINT = 'MARKETO_ENDPOINT',
  MARKETO_IDENTITY = 'MARKETO_IDENTITY',
  MONDAY_API_TOKEN = 'MONDAY_API_TOKEN',
  ORACLE_CLOUD_URL = 'ORACLE_CLOUD_URL',
  ORACLE_PASSWORD = 'ORACLE_PASSWORD',
  ORACLE_USERNAME = 'ORACLE_USERNAME',
  SAGE_INTACCT_COMPANY_ID = 'SAGE_INTACCT_COMPANY_ID',
  SAGE_INTACCT_USER_ID = 'SAGE_INTACCT_USER_ID',
  SAGE_INTACCT_USER_PASSWORD = 'SAGE_INTACCT_USER_PASSWORD',
  SAILTHRU_COMPANY_KEY = 'SAILTHRU_COMPANY_KEY',
  SAILTHRU_COMPANY_SECRET = 'SAILTHRU_COMPANY_SECRET',
  SERVICENOW_PASSWORD = 'SERVICENOW_PASSWORD',
  SERVICENOW_SUBDOMAIN = 'SERVICENOW_SUBDOMAIN',
  SERVICENOW_USERNAME = 'SERVICENOW_USERNAME',
  TABLEAU_PERSONAL_ACCESS_TOKEN_NAME = 'TABLEAU_PERSONAL_ACCESS_TOKEN_NAME',
  TABLEAU_PERSONAL_ACCESS_TOKEN_SECRET = 'TABLEAU_PERSONAL_ACCESS_TOKEN_SECRET',
  TABLEAU_SERVER_NAME = 'TABLEAU_SERVER_NAME',
  TABLEAU_SITE_ID = 'TABLEAU_SITE_ID',
  TRELLO_API_KEY = 'TRELLO_API_KEY',
  TRELLO_API_TOKEN = 'TRELLO_API_TOKEN',
  WOOCOMMERCE_CONSUMER_KEY = 'WOOCOMMERCE_CONSUMER_KEY',
  WOOCOMMERCE_CONSUMER_SECRET = 'WOOCOMMERCE_CONSUMER_SECRET',
  WOOCOMMERCE_STORE_DOMAIN = 'WOOCOMMERCE_STORE_DOMAIN',
  WORKABLE_API_ACCESS_TOKEN = 'WORKABLE_API_ACCESS_TOKEN',
  WORKABLE_ACCOUNT_SUBDOMAIN = 'WORKABLE_ACCOUNT_SUBDOMAIN',
  ZOHO_CRM_ACCOUNTS_SERVER = 'ZOHO_CRM_ACCOUNTS_SERVER',
  ZOHO_CRM_API_DOMAIN = 'ZOHO_CRM_API_DOMAIN',
}

export type DataSource =
  | DynamicDataSource<any>
  | StaticEnumDataSource
  | FieldMapperDataSource
  | ComboInputDataSource;

type BasicDataSource = {
  /**
   * title for data source
   */
  title: string;

  /**
   * description for data source
   */
  subtitle?: ReactNode;

  /**
   * If specified, instructionalText replaces the default text explaining the input under the selection,
   * e.g. "Let your users select <input name> from their <integration name> account."
   */
  instructionalText?: ReactNode;

  /**
   * If true, this property hides this data source as a Connect field type.
   * @default false
   */
  hideFromConnectFieldTypes?: boolean;

  /**
   * If true, dataSource will be fetched in pagination format
   * @default false
   */
  supportPagination?: boolean;
};

type DynamicDataSource<TValues = EnumInputValue[]> = BasicDataSource & {
  type: DataSourceType.DYNAMIC;

  /**
   * If provided, the response from the refresh method will be saved into the step.
   */
  cacheKey?: string;

  /**
   * This array is used to determine whether or not the input's values should be
   * refreshed. Strings can be used to specify a KeyedSource dependency within
   * `options.actionParameters`, and a function can be used to specify any other
   * dependency within the step's parameters.
   *
   * If `refreshDependencies` is an empty array, the input will refresh on mount.
   * If `refreshDependencies` is not provided, the input will not refresh unless
   * manually invoked (i.e., by a button).
   */
  refreshDependencies?: (string | ((options: ActionStepParameters) => any))[];

  /**
   * When the data comes back from the intent requested by the `refresh` method,
   * this is the method used to map the data into the input + cache
   */
  mapRefreshToValues: (response: any) => TValues;

  /**
   * This method is responsible for specifying the action, intent and parameters
   * for loading the data into the input
   */
  getRefreshActionParameters: (
    options: ActionStepParameters,
    inputs: SidebarInput[],
    activeInput: SidebarInput,
  ) => ActionStepParameters;

  /**
   * called after the input refreshes w/ the values mapped from `mapRefreshToValues`
   */
  onRefresh?: (value: any) => ActionResponse;
};

type ActionResponse = AlertActionResponse | DispatchActionResponse;
type AlertActionResponse = {
  type: ActionResponseType.ALERT;
  status: ActionResponseStatus;
  message: string;
};

type DispatchActionResponse = {
  type: ActionResponseType.DISPATCH;
  status: ActionResponseStatus;
  id: string;
  source: Source;
};
enum ActionResponseStatus {
  INFO = 'INFO',
  ERROR = 'ERROR',
  SUCCESS = 'SUCCESS',
}

enum ActionResponseType {
  NONE = 'NONE',
  RE_AUTHENTICATE = 'RE_AUTHENTICATE',
  ALERT = 'ALERT',
  DISPATCH = 'DISPATCH',
}

type StaticEnumDataSource = BasicDataSource & {
  type: DataSourceType.STATIC_ENUM;
  /**
   * id for data source
   */
  id: string;
  values: EnumInputValue[] | EnumSection[];
};

/**
 * A FieldMapperDataSource refers to a source that is able to drive an input that is responsible
 * for mapping remote/integration fields to local/project fields.
 */
type FieldMapperDataSource = BasicDataSource & {
  id: string;
  type: DataSourceType.FIELD_MAPPER;
  recordSource: DynamicDataSource<(EnumSection | EnumInputValue)[]>;
  fieldSource: DynamicDataSource<(EnumSection | EnumInputValue)[]>;
};

/**
 * A ComboInputDataSource refers to a source that is able to drive an input that is responsible
 * for combining the remote/integration fields to local/project fields.
 */
type ComboInputDataSource = BasicDataSource & {
  id: string;
  type: DataSourceType.COMBO_INPUT;
  mainInputSource: DynamicDataSource<(EnumSection | EnumInputValue)[]>;
  dependentInputSource: DynamicDataSource<(EnumSection | EnumInputValue)[]>;
  fieldSource?: DynamicDataSource<(EnumSection | EnumInputValue)[]>;
};

type BaseActionStepParameters = {
  actionType: string;
  credentials: string[];
  retryOnFailure?: boolean;
  ignoreFailure?: boolean;
};

enum DataSourceType {
  DYNAMIC = 'DYNAMIC_DATA_SOURCE',
  STATIC_ENUM = 'STATIC_ENUM_DATA_SOURCE',
  FIELD_MAPPER = 'FIELD_MAPPER_DATA_SOURCE',
  COMBO_INPUT = 'COMBO_INPUT_DATA_SOURCE',
}

type ActionStepParameters = BaseActionStepParameters & {
  intent: string;
  actionParameters: KeyedSource<DataType.ANY>[];
};

/**
 * It will override the action alias for making files name more cleaner
 */
export const overrideActionAlias: Partial<Record<string, string>> = {
  monday: 'monday',
};

export type ActionConfigDTO = {
  name: string;
  brandColor: string;
  provider: string | undefined;
  authScheme: AuthenticationScheme | undefined;
  endUserSuppliedValues: IntegrationConnectInput[] | undefined;
  isAccountTypeSupported: boolean;
};